// Sample data for the Ramadan planner
// In a real application, this would come from an API or database
import { useTranslation } from 'react-i18next';

// Types
export type VerseType = {
  arabic: string;
  translationKey: string;
};

export type HadithType = {
  arabic?: string;
  textKey: string;
  referenceKey: string;
};

export type DuaType = {
  arabic: string;
  translationKey: string;
};

export type NameOfAllahType = {
  arabic: string;
  meaningKey: string;
};

export type DailyContentType = {
  verse: VerseType;
  hadith: HadithType;
  dua: DuaType;
  namesOfAllah: NameOfAllahType[];
  taskOfTheDay: string;
};

// Sample data structure with arabic content and translation keys
const dailyDataStructure: { [key: number]: {
  day: number;
  verse: {
    arabic: string;
    translationKey: string;
  };
  hadith: {
    arabic?: string;
    textKey: string;
    referenceKey: string;
  };
  dua: {
    arabic: string;
    translationKey: string;
  };
  namesOfAllah: Array<{
    arabic: string;
    meaningKey: string;
  }>;
  taskOfTheDay: string;
}} = {
  1: {
    "day": 1,
    "verse": {
      "arabic": "وَمَا خَلَقْتُ الْجِنَّ وَالْإِنْسَ إِلَّا لِيَعْبُدُونِ [الذاريات : ٥٦]",
      "translationKey": "dailyContent.day1.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day1.hadith.text",
      "referenceKey": "dailyContent.day1.hadith.reference"
    },
    "dua": {
      "arabic": "رَبَّنَا آتِنَا فِي الدُّنْيَا حَسَنَةً وَ فِي الْآخِرَةِ حَسَنَةً وَقِنَا عَذَابَ النَّارِ",
      "translationKey": "dailyContent.day1.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الرَّحْمٰنُ",
        "meaningKey": "dailyContent.day1.namesOfAllah.1"
      },
      {
        "arabic": "الرَّحِيمُ",
        "meaningKey": "dailyContent.day1.namesOfAllah.2"
      },
      {
        "arabic": "الله",
        "meaningKey": "dailyContent.day1.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day1.taskOfTheDay"
  },
  2: {
    "day": 2,
    "verse": {
      "arabic": "أَفَحَسِبْتُمْ أَنَّمَا خَلَقْنَاكُمْ عَبَثًا وَأَنَّكُمْ إِلَيْنَا لَا تُرْجَعُونَ [المؤمنون: ١١٥]",
      "translationKey": "dailyContent.day2.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day2.hadith.text",
      "referenceKey": "dailyContent.day2.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَسْئَلُكَ الْهُدَى وَالتَّقَى وَالْعَفَافَ وَالْغِنَى",
      "translationKey": "dailyContent.day2.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمَلِكُ",
        "meaningKey": "dailyContent.day2.namesOfAllah.1"
      },
      {
        "arabic": "الْقُدُّوسُ",
        "meaningKey": "dailyContent.day2.namesOfAllah.2"
      },
      {
        "arabic": "السلام",
        "meaningKey": "dailyContent.day2.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day2.taskOfTheDay"
  },
  3: {
    "day": 3,
    "verse": {
      "arabic": "وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَأَطِيعُوا الرَّسُولَ لَعَلَّكُمْ تُرْحَمُونَ [النور : ٥٦]",
      "translationKey": "dailyContent.day3.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day3.hadith.text",
      "referenceKey": "dailyContent.day3.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ لَكَ صُمْتُ، وَعَلَى رِزْقِكَ أَفْطَرْتُ",
      "translationKey": "dailyContent.day3.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمُؤْمِنُ",
        "meaningKey": "dailyContent.day3.namesOfAllah.1"
      },
      {
        "arabic": "الْمُهَيْمِنُ",
        "meaningKey": "dailyContent.day3.namesOfAllah.2"
      },
      {
        "arabic": "الْعَزِيزُ",
        "meaningKey": "dailyContent.day3.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day3.taskOfTheDay"
  },
  4: {
    "day": 4,
    "verse": {
      "arabic": "يَا أَيُّهَا الَّذِينَ آمَنُوا كُتِبَ عَلَيْكُمُ الصِّيَامُ كَمَا كُتِبَ عَلَى الَّذِينَ مِنْ قَبْلِكُمْ لَعَلَّكُمْ تَتَّقُوْنَ [البقرة : ١٨٣]",
      "translationKey": "dailyContent.day4.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day4.hadith.text",
      "referenceKey": "dailyContent.day4.hadith.reference"
    },
    "dua": {
      "arabic": "ذَهَبَ الظَّمَأُ وَابْتَلَّتِ الْعُرُوقُ، وَثَبَتَ الْأَجْرُ إِنْ شَاءَ اللَّهُ",
      "translationKey": "dailyContent.day4.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْجَبَّارُ",
        "meaningKey": "dailyContent.day4.namesOfAllah.1"
      },
      {
        "arabic": "الْمُتَكَبِّرُ",
        "meaningKey": "dailyContent.day4.namesOfAllah.2"
      },
      {
        "arabic": "الْخَالِقُ",
        "meaningKey": "dailyContent.day4.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day4.taskOfTheDay"
  },
  5: {
    "day": 5,
    "verse": {
      "arabic": "يَمْحَقُ اللَّهُ الرِّبَا وَيُرْبِي الصَّدَقَاتِ ۗ وَاللَّهُ لَا يُحِبُّ كُلَّ كَفَّارٍ أَثِيمٍ [البقرة : ٢٧٦]",
      "translationKey": "dailyContent.day5.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day5.hadith.text",
      "referenceKey": "dailyContent.day5.hadith.reference"
    },
    "dua": {
      "arabic": "رَبَّنَا لَا تُزِغْ قُلُوبَنَا بَعْدَ إِذْ هَدَيْتَنَا وَهَبْ لَنَا مِنْ لَدُنْكَ رَحْمَةً ۚ إِنَّكَ أَنْتَ الْوَهَّابُ",
      "translationKey": "dailyContent.day5.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْبَارِئُ",
        "meaningKey": "dailyContent.day5.namesOfAllah.1"
      },
      {
        "arabic": "الْمُصَوِّرُ",
        "meaningKey": "dailyContent.day5.namesOfAllah.2"
      },
      {
        "arabic": "الْغَفَّارُ",
        "meaningKey": "dailyContent.day5.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day5.taskOfTheDay"
  },
  6: {
    "day": 6,
    "verse": {
      "arabic": "تَعَاوَنُوا عَلَى الْبِرِّ وَالتَّقْوَى وَلَا تَعَاوَنُوا عَلَى الْإِثْمِ وَالْعُدْوَانِ ۚ وَاتَّقُوا اللَّهَ ۖ إِنَّ اللَّهَ شَدِيدُ الْعِقَابِ [المائدة : ٢]",
      "translationKey": "dailyContent.day6.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day6.hadith.text",
      "referenceKey": "dailyContent.day6.hadith.reference"
    },
    "dua": {
      "arabic": "بِسْمِ اللَّهِ الَّذِي لَا يَضُرُّ مَعَ اسْمِهِ شَيْءٌ فِي الْأَرْضِ وَلَا فِي السَّمَاءِ ۚ وَهُوَ السَّمِيعُ الْعَلِيمُ",
      "translationKey": "dailyContent.day6.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْقَهَّارُ",
        "meaningKey": "dailyContent.day6.namesOfAllah.1"
      },
      {
        "arabic": "الْوَهَّابُ",
        "meaningKey": "dailyContent.day6.namesOfAllah.2"
      },
      {
        "arabic": "الرَّزَّاقُ",
        "meaningKey": "dailyContent.day6.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day6.taskOfTheDay"
  },
  7: {
    "day": 7,
    "verse": {
      "arabic": "يَا أَيُّهَا الَّذِينَ آمَنُوا اتَّقُوا اللَّهَ وَكُونُوا مَعَ الصَّادِقِينَ [التوبة : ١١٩]",
      "translationKey": "dailyContent.day7.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day7.hadith.text",
      "referenceKey": "dailyContent.day7.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْكُفْرِ وَالْفَقْرِ اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنْ عَذَابِ الْقَبْرِ لَا إِلَهَ إِلَّا أَنْتَ",
      "translationKey": "dailyContent.day7.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْوَاسِعُ",
        "meaningKey": "dailyContent.day7.namesOfAllah.1"
      },
      {
        "arabic": "الْحَكِيمُ",
        "meaningKey": "dailyContent.day7.namesOfAllah.2"
      },
      {
        "arabic": "الْوَدُودُ",
        "meaningKey": "dailyContent.day7.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day7.taskOfTheDay"
  },
  8: {
    "day": 8,
    "verse": {
      "arabic": "وَأَقِيمُوا الصَّلَاةَ وَآتُوا الزَّكَاةَ وَارْكَعُوا مَعَ الرَّاكِعِينَ [البقرة : ٤٣]",
      "translationKey": "dailyContent.day8.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day8.hadith.text",
      "referenceKey": "dailyContent.day8.hadith.reference"
    },
    "dua": {
      "arabic": "رَبِّ ارْحَمْهُمَا كَمَا رَبَّيَانِي صَغِيرًا",
      "translationKey": "dailyContent.day8.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمَجِيدُ",
        "meaningKey": "dailyContent.day8.namesOfAllah.1"
      },
      {
        "arabic": "الأحد",
        "meaningKey": "dailyContent.day8.namesOfAllah.2"
      },
      {
        "arabic": "الشَّهِيدُ",
        "meaningKey": "dailyContent.day8.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day8.taskOfTheDay"
  },
  9: {
    "day": 9,
    "verse": {
      "arabic": "يَا أَيُّهَا الَّذِينَ آمَنُوا اتَّقُوا اللَّهَ حَقَّ تُقَاتِهِ وَلَا تَمُوتُنَّ إِلَّا وَأَنْتُمْ مُسْلِمُونَ [آل عمران : ١٠٢]",
      "translationKey": "dailyContent.day9.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day9.hadith.text",
      "referenceKey": "dailyContent.day9.hadith.reference"
    },
    "dua": {
      "arabic": "رَبَّنَا أَفْرِغْ عَلَيْنَا صَبْرًا وَتَوَفَّنَا مُسْلِمِينَ",
      "translationKey": "dailyContent.day9.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْحَقُّ",
        "meaningKey": "dailyContent.day9.namesOfAllah.1"
      },
      {
        "arabic": "الْوَكِيلُ",
        "meaningKey": "dailyContent.day9.namesOfAllah.2"
      },
      {
        "arabic": "الْقَوِيُّ",
        "meaningKey": "dailyContent.day9.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day9.taskOfTheDay"
  },
  10: {
    "day": 10,
    "verse": {
      "arabic": "وَمَنْ يَبْتَغِ غَيْرَ الْإِسْلَامِ دِينًا فَلَنْ يُقْبَلَ مِنْهُ وَهُوَ فِي الْآخِرَةِ مِنَ الْخَاسِرِينَ [آل عمران : ٨٥]",
      "translationKey": "dailyContent.day10.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day10.hadith.text",
      "referenceKey": "dailyContent.day10.hadith.reference"
    },
    "dua": {
      "arabic": "رَبَّنَا هَبْ لَنَا مِنْ أَزْوَاجِنَا وَذُرِّيَّاتِنَا قُرَّةَ أَعْيُنٍ وَاجْعَلْنَا لِلْمُتَّقِينَ إِمَامًا",
      "translationKey": "dailyContent.day10.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمَتِينُ",
        "meaningKey": "dailyContent.day10.namesOfAllah.1"
      },
      {
        "arabic": "الْوَلِيُّ",
        "meaningKey": "dailyContent.day10.namesOfAllah.2"
      },
      {
        "arabic": "الْحَمِيدُ",
        "meaningKey": "dailyContent.day10.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day10.taskOfTheDay"
  },
  11: {
    "day": 11,
    "verse": {
      "arabic": "وَمَا أُمِرُوا إِلَّا لِيَعْبُدُوا اللَّهَ مُخْلِصِينَ لَهُ الدِّينَ حُنَفَاءَ وَيُقِيمُوا الصَّلَاةَ وَيُؤْتُوا الزَّكَاةَ ۚ وَذَٰلِكَ دِينُ الْقَيِّمَةِ [البينة : ٥]",
      "translationKey": "dailyContent.day11.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day11.hadith.text",
      "referenceKey": "dailyContent.day11.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ اكْفِنِي بِحَلَالِكَ عَنْ حَرَامِكَ، وَأَغْنِنِي بِفَضْلِكَ عَمَّنْ سِوَاكَ",
      "translationKey": "dailyContent.day11.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الطَّيِّبُ",
        "meaningKey": "dailyContent.day11.namesOfAllah.1"
      },
      {
        "arabic": "الْقَدِيرُ",
        "meaningKey": "dailyContent.day11.namesOfAllah.2"
      },
      {
        "arabic": "الرَّبُّ",
        "meaningKey": "dailyContent.day11.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day11.taskOfTheDay"
  },
  12: {
    "day": 12,
    "verse": {
      "arabic": "إِنَّمَا وَلِيُّكُمُ اللَّهُ وَرَسُولُهُ وَالَّذِينَ آمَنُوا الَّذِينَ يُقِيمُونَ الصَّلَاةَ وَيُؤْتُونَ الزَّكَاةَ وَهُمْ رَاكِعُونَ [المائدة : ٥٥]",
      "translationKey": "dailyContent.day12.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day12.hadith.text",
      "referenceKey": "dailyContent.day12.hadith.reference"
    },
    "dua": {
      "arabic": "رَبِّ أَوْزِعْنِي أَنْ أَشْكُرَ نِعْمَتَكَ الَّتِي أَنْعَمْتَ عَلَيَّ وَعَلَى وَالِدَيَّ وَأَنْ أَعْمَلَ صَالِحًا تَرْضَاهُ وَأَدْخِلْنِي بِرَحْمَتِكَ فِي عِبَادِكَ الصَّالِحِينَ",
      "translationKey": "dailyContent.day12.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْقَرِيبُ",
        "meaningKey": "dailyContent.day12.namesOfAllah.1"
      },
      {
        "arabic": "الْخَلَّاقُ",
        "meaningKey": "dailyContent.day12.namesOfAllah.2"
      },
      {
        "arabic": "الْحَيُّ",
        "meaningKey": "dailyContent.day12.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day12.taskOfTheDay"
  },
  13: {
    "day": 13,
    "verse": {
      "arabic": "إِنَّمَا الْمُؤْمِنُونَ الَّذِينَ إِذَا ذُكِرَ اللَّهُ وَجِلَتْ قُلُوبُهُمْ وَإِذَا تُلِيَتْ عَلَيْهِمْ آيَاتُهُ زَادَتْهُمْ إِيمَانًا وَعَلَىٰ رَبِّهِمْ يَتَوَكَّلُونَ [الأنفال : ٢]",
      "translationKey": "dailyContent.day13.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day13.hadith.text",
      "referenceKey": "dailyContent.day13.hadith.reference"
    },
    "dua": {
      "arabic": "لَا إِلَٰهَ إِلَّا أَنْتَ سُبْحَانَكَ إِنِّي كُنْتُ مِنَ الظَّالِمِينَ",
      "translationKey": "dailyContent.day13.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْقَيُّومُ",
        "meaningKey": "dailyContent.day13.namesOfAllah.1"
      },
      {
        "arabic": "الْمُبِينُ",
        "meaningKey": "dailyContent.day13.namesOfAllah.2"
      },
      {
        "arabic": "الْقَرِيبُ",
        "meaningKey": "dailyContent.day13.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day13.taskOfTheDay"
  },
  14: {
    "day": 14,
    "verse": {
      "arabic": "قُلْ إِنَّ صَلَاتِي وَنُسُكِي وَمَحْيَايَ وَمَمَاتِي لِلَّهِ رَبِّ الْعَالَمِينَ [الأنعام : ١٦٢]",
      "translationKey": "dailyContent.day14.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day14.hadith.text",
      "referenceKey": "dailyContent.day14.hadith.reference"
    },
    "dua": {
      "arabic": "رَبَّنَا ظَلَمْنَا أَنْفُسَنَا وَإِنْ لَّمْ تَغْفِرْ لَنَا وَتَرْحَمْنَا لَنَكُونَنَّ مِنَ الْخَاسِرِينَ",
      "translationKey": "dailyContent.day14.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْبَاسِطُ",
        "meaningKey": "dailyContent.day14.namesOfAllah.1"
      },
      {
        "arabic": "الْوِتْرُ",
        "meaningKey": "dailyContent.day14.namesOfAllah.2"
      },
      {
        "arabic": "الشَّاكِرُ",
        "meaningKey": "dailyContent.day14.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day14.taskOfTheDay"
  },
  15: {
    "day": 15,
    "verse": {
      "arabic": "وَلَقَدْ أُوحِيَ إِلَيْكَ وَإِلَى الَّذِينَ مِنْ قَبْلِكَ لَئِنْ أَشْرَكْتَ لَيَحْبَطَنَّ عَمَلُكَ وَلَتَكُونَنَّ مِنَ الْخَاسِرِينَ [الزمر : ٦٥]",
      "translationKey": "dailyContent.day15.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day15.hadith.text",
      "referenceKey": "dailyContent.day15.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنْ عَذَابِ الْقَبْرِ وَ مِنْ عَذَابِ النَّارِ وَ مِنْ فِتْنَةِ الْمَحْيَا وَالْمَمَاتِ وَاَعُوْذُبِكَ مِنْ فِتْنَةِ المَسِيحِ الدَّجَّالِ",
      "translationKey": "dailyContent.day15.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْكَافِي",
        "meaningKey": "dailyContent.day15.namesOfAllah.1"
      },
      {
        "arabic": "الشَّافِي",
        "meaningKey": "dailyContent.day15.namesOfAllah.2"
      },
      {
        "arabic": "السَّمِيعُ",
        "meaningKey": "dailyContent.day15.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day15.taskOfTheDay"
  },
  16: {
    "day": 16,
    "verse": {
      "arabic": "مَا آتَاكُمُ الرَّسُولُ فَخُذُوهُ وَمَا نَهَاكُمْ عَنْهُ فَانْتَهُوا ۚ وَاتَّقُوا اللَّهَ ۖ إِنَّ اللَّهَ شَدِيدُ الْعِقَابِ [الحشر : ٧]",
      "translationKey": "dailyContent.day16.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day16.hadith.text",
      "referenceKey": "dailyContent.day16.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنْ مُنْكَرَاتِ الْأَخْلَاقِ وَالْأَعْمَالِ وَالْأَهْوَاءِ",
      "translationKey": "dailyContent.day16.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْبَصِيرُ",
        "meaningKey": "dailyContent.day16.namesOfAllah.1"
      },
      {
        "arabic": "الْحَكَمُ",
        "meaningKey": "dailyContent.day16.namesOfAllah.2"
      },
      {
        "arabic": "النَّصِيرُ",
        "meaningKey": "dailyContent.day16.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day16.taskOfTheDay"
  },
  17: {
    "day": 17,
    "verse": {
      "arabic": "يَا أَيُّهَا الَّذِينَ آمَنُوا إِنْ جَاءَكُمْ فَاسِقٌ بِنَبَإٍ فَتَبَيَّنُوا أَنْ تُصِيبُوا قَوْمًا بِجَهَالَةٍ فَتُصْبِحُوا عَلَىٰ مَا فَعَلْتُمْ نَادِمِينَ [الحجرات : ٦]",
      "translationKey": "dailyContent.day17.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day17.hadith.text",
      "referenceKey": "dailyContent.day17.hadith.reference"
    },
    "dua": {
      "arabic": "رَبِّ أَدْخِلْنِي مُدْخَلَ صِدْقٍ وَأَخْرِجْنِي مُخْرَجَ صِدْقٍ وَاجْعَلْ لِي مِنْ لَدُنْكَ سُلْطَانًا نَصِيرًا",
      "translationKey": "dailyContent.day17.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "اللَّطِيفُ",
        "meaningKey": "dailyContent.day17.namesOfAllah.1"
      },
      {
        "arabic": "الْخَبِيرُ",
        "meaningKey": "dailyContent.day17.namesOfAllah.2"
      },
      {
        "arabic": "الْحَلِيمُ",
        "meaningKey": "dailyContent.day17.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day17.taskOfTheDay"
  },
  18: {
    "day": 18,
    "verse": {
      "arabic": "يَا أَيُّهَا الَّذِينَ آمَنُوا اجْتَنِبُوا كَثِيرًا مِنَ الظَّنِّ إِنَّ بَعْضَ الظَّنِّ إِثْمٌ [الحجرات : ١٢]",
      "translationKey": "dailyContent.day18.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day18.hadith.text",
      "referenceKey": "dailyContent.day18.hadith.reference"
    },
    "dua": {
      "arabic": "رَبِّ اجْعَلْنِي مُقِيمَ الصَّلَاةِ وَمِنْ ذُرِّيَّتِي ۚ رَبَّنَا وَتَقَبَّلْ دُعَاءِ",
      "translationKey": "dailyContent.day18.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "العَظِيمُ",
        "meaningKey": "dailyContent.day18.namesOfAllah.1"
      },
      {
        "arabic": "الْغَفُورُ",
        "meaningKey": "dailyContent.day18.namesOfAllah.2"
      },
      {
        "arabic": "الشَّكُورُ",
        "meaningKey": "dailyContent.day18.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day18.taskOfTheDay"
  },
  19: {
    "day": 19,
    "verse": {
      "arabic": "قُلْ هَلْ يَسْتَوِي الَّذِينَ يَعْلَمُونَ وَالَّذِينَ لَا يَعْلَمُونَ ۗ إِنَّمَا يَتَذَكَّرُ أُولُو الْأَلْبَابِ [الزمر : ٩]",
      "translationKey": "dailyContent.day19.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day19.hadith.text",
      "referenceKey": "dailyContent.day19.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنْ جَهْدِ الْبَلَاءِ وَدَرَكِ الشَّقَاءِ، وَسُوءِ الْقَضَاءِ ، وَشَمَاتَةِ الْأَعْدَاءِ",
      "translationKey": "dailyContent.day19.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْعَلِيُّ",
        "meaningKey": "dailyContent.day19.namesOfAllah.1"
      },
      {
        "arabic": "الْكَبِيرُ",
        "meaningKey": "dailyContent.day19.namesOfAllah.2"
      },
      {
        "arabic": "الْحَفِيظُ",
        "meaningKey": "dailyContent.day19.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day19.taskOfTheDay"
  },
  20: {
    "day": 20,
    "verse": {
      "arabic": "قُلْ يَا عِبَادِيَ الَّذِينَ أَسْرَفُوا عَلَىٰ أَنْفُسِهِمْ لَا تَقْنَطُوا مِنْ رَحْمَةِ اللَّهِ ۚ إِنَّ اللَّهَ يَغْفِرُ الذُّنُوبَ جَمِيعًا ۚ إِنَّهُ هُوَ الْغَفُورُ الرَّحِيمُ [الزمر : ٥٣]",
      "translationKey": "dailyContent.day20.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day20.hadith.text",
      "referenceKey": "dailyContent.day20.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ مِنَ الْأَرْبَعِ مِنْ عِلْمٍ لَا يَنْفَعُ وَمِنْ قَلْبٍ لَا يَخْشَعُ، وَمِنْ نَفْسٍ لَا تَشْبَعُ، وَمِنْ دُعَاءٍ لَا يُسْمَعُ",
      "translationKey": "dailyContent.day20.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمُقِيتُ",
        "meaningKey": "dailyContent.day20.namesOfAllah.1"
      },
      {
        "arabic": "الْحَسِيبُ",
        "meaningKey": "dailyContent.day20.namesOfAllah.2"
      },
      {
        "arabic": "الْجَمِيلُ",
        "meaningKey": "dailyContent.day20.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day20.taskOfTheDay"
  },
  21: {
    "day": 21,
    "verse": {
      "arabic": "إِنْ تَجْتَنِبُوا كَبَائِرَ مَا تُنْهَوْنَ عَنْهُ نُكَفِّرْ عَنْكُمْ سَيِّئَاتِكُمْ وَنُدْخِلْكُمْ مُدْخَلًا كَرِيمًا [النساء : ٣١]",
      "translationKey": "dailyContent.day21.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day21.hadith.text",
      "referenceKey": "dailyContent.day21.hadith.reference"
    },
    "dua": {
      "arabic": "رَبِّ اغْفِرْ لِي وَتُبْ عَلَيَّ ۖ إِنَّكَ أَنْتَ التَّوَّابُ الرَّحِيمُ",
      "translationKey": "dailyContent.day21.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْكَرِيمُ",
        "meaningKey": "dailyContent.day21.namesOfAllah.1"
      },
      {
        "arabic": "الْوَاحِدُ",
        "meaningKey": "dailyContent.day21.namesOfAllah.2"
      },
      {
        "arabic": "الصَّمَدُ",
        "meaningKey": "dailyContent.day21.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day21.taskOfTheDay"
  },
  22: {
    "day": 22,
    "verse": {
      "arabic": "وَالَّذِينَ جَاهَدُوا فِينَا لَنَهْدِيَنَّهُمْ سُبُلَنَا ۚ وَإِنَّ اللَّهَ لَمَعَ الْمُحْسِنِينَ [العنكبوت : ٦٩]",
      "translationKey": "dailyContent.day22.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day22.hadith.text",
      "referenceKey": "dailyContent.day22.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ أَعِنِّي عَلَى ذِكْرِكَ، وَشُكْرِكَ، وَحُسْنِ عِبَادَتِكَ",
      "translationKey": "dailyContent.day22.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْقَابِضُ",
        "meaningKey": "dailyContent.day22.namesOfAllah.1"
      },
      {
        "arabic": "الْمُقْتَدِرُ",
        "meaningKey": "dailyContent.day22.namesOfAllah.2"
      },
      {
        "arabic": "الْمُقَدِّمُ",
        "meaningKey": "dailyContent.day22.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day22.taskOfTheDay"
  },
  23: {
    "day": 23,
    "verse": {
      "arabic": "يَا أَيُّهَا الَّذِينَ آمَنُوا لَا تَتَّخِذُوا الْكَافِرِينَ أَوْلِيَاءَ مِنْ دُونِ الْمُؤْمِنِينَ ۚ أَتُرِيدُونَ أَنْ تَجْعَلُوا لِلَّهِ عَلَيْكُمْ سُلْطَانًا مُبِينًا [النساء : ١٤٤]",
      "translationKey": "dailyContent.day23.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day23.hadith.text",
      "referenceKey": "dailyContent.day23.hadith.reference"
    },
    "dua": {
      "arabic": "يَا مُقَلِّبَ الْقُلُوبِ ثَبِّتْ قَلْبِي عَلَى دِينِكَ",
      "translationKey": "dailyContent.day23.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمُؤَخِّرُ",
        "meaningKey": "dailyContent.day23.namesOfAllah.1"
      },
      {
        "arabic": "الأَوَّلُ",
        "meaningKey": "dailyContent.day23.namesOfAllah.2"
      },
      {
        "arabic": "الْآخِرُ",
        "meaningKey": "dailyContent.day23.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day23.taskOfTheDay"
  },
  24: {
    "day": 24,
    "verse": {
      "arabic": "قُلْ هَٰذِهِ سَبِيلِي أَدْعُو إِلَى اللَّهِ ۚ عَلَىٰ بَصِيرَةٍ أَنَا وَمَنِ اتَّبَعَنِي ۖ وَسُبْحَانَ اللَّهِ وَمَا أَنَا مِنَ الْمُشْرِكِينَ [يوسف : ١٠٨]",
      "translationKey": "dailyContent.day24.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day24.hadith.text",
      "referenceKey": "dailyContent.day24.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَعُوذُ بِكَ أَنْ أُشْرِكَ بِكَ وَأَنَا أَعْلَمُ، وَأَسْتَغْفِرُكَ لِمَا لَا أَعْلَمُ",
      "translationKey": "dailyContent.day24.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الظَّاهِرُ",
        "meaningKey": "dailyContent.day24.namesOfAllah.1"
      },
      {
        "arabic": "الْبَاطِنُ",
        "meaningKey": "dailyContent.day24.namesOfAllah.2"
      },
      {
        "arabic": "الْعَلِيمُ",
        "meaningKey": "dailyContent.day24.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day24.taskOfTheDay"
  },
  25: {
    "day": 25,
    "verse": {
      "arabic": "وَالْعَصْرِ إِنَّ الْإِنْسَانَ لَفِي خُسْرٍ إِلَّا الَّذِينَ آمَنُوا وَعَمِلُوا الصَّالِحَاتِ وَتَوَاصَوْا بِالْحَقِّ وَتَوَاصَوْا بِالصَّبْرِ",
      "translationKey": "dailyContent.day25.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day25.hadith.text",
      "referenceKey": "dailyContent.day25.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ بَاعِدْ بَيْنِي وَبَيْنَ خَطَايَايَ كَمَا بَاعَدْتَ بَيْنَ الْمَشْرِقِ وَالْمَغْرِبِ اللَّهُمَّ نَقِّنِي مِنَ الْخَطَايَا كَمَا يُنَقَّى الثَّوْبُ الْأَبْيَضُ مِنَ الدَّنَسِ اللَّهُمَّ اغْسِلْنِي مِنْ خَطَايَايَ بِالْمَاءِ وَالثَّلْجِ وَالْبَرَدِ",
      "translationKey": "dailyContent.day25.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمُتَعَالِي",
        "meaningKey": "dailyContent.day25.namesOfAllah.1"
      },
      {
        "arabic": "الْبَرُّ",
        "meaningKey": "dailyContent.day25.namesOfAllah.2"
      },
      {
        "arabic": "التَّوَّابُ",
        "meaningKey": "dailyContent.day25.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day25.taskOfTheDay"
  },
  26: {
    "day": 26,
    "verse": {
      "arabic": "وَأَطِيعُوا اللَّهَ وَرَسُولَهُ وَلَا تَنَازَعُوا فَتَفْشَلُوا وَتَذْهَبَ رِيحُكُمْ ۖ وَاصْبِرُوا ۚ إِنَّ اللَّهَ مَعَ الصَّابِرِينَ [الأنفال : ٤٦]",
      "translationKey": "dailyContent.day26.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day26.hadith.text",
      "referenceKey": "dailyContent.day26.hadith.reference"
    },
    "dua": {
      "arabic": "رَبَّنَا اغْفِرْ لِي وَلِوَالِدَيَّ وَلِلْمُؤْمِنِينَ يَوْمَ يَقُومُ الْحِسَابُ",
      "translationKey": "dailyContent.day26.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمَلِيكُ",
        "meaningKey": "dailyContent.day26.namesOfAllah.1"
      },
      {
        "arabic": "الْعَفُوُّ",
        "meaningKey": "dailyContent.day26.namesOfAllah.2"
      },
      {
        "arabic": "الرَّؤُوفُ",
        "meaningKey": "dailyContent.day26.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day26.taskOfTheDay"
  },
  27: {
    "day": 27,
    "verse": {
      "arabic": "أَفَتُؤْمِنُونَ بِبَعْضِ الْكِتَابِ وَتَكْفُرُونَ بِبَعْضٍ ۚ فَمَا جَزَاءُ مَنْ يَفْعَلُ ذَٰلِكَ مِنْكُمْ إِلَّا خِزْيٌ فِي الْحَيَاةِ الدُّنْيَا ۖ وَيَوْمَ الْقِيَامَةِ يُرَدُّونَ إِلَىٰ أَشَدِّ الْعَذَابِ ۗ وَمَا اللَّهُ بِغَافِلٍ عَمَّا تَعْمَلُونَ [البقرة : ٨٥]",
      "translationKey": "dailyContent.day27.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day27.hadith.text",
      "referenceKey": "dailyContent.day27.hadith.reference"
    },
    "dua": {
      "arabic": "أَعُوذُ بِكَلِمَاتِ اللَّهِ التَّامَّاتِ مِنْ شَرِّ مَا خَلَقَ",
      "translationKey": "dailyContent.day27.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمَالِكُ",
        "meaningKey": "dailyContent.day27.namesOfAllah.1"
      },
      {
        "arabic": "الأَكْرَمُ",
        "meaningKey": "dailyContent.day27.namesOfAllah.2"
      },
      {
        "arabic": "الْمُعْطِي",
        "meaningKey": "dailyContent.day27.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day27.taskOfTheDay"
  },
  28: {
    "day": 28,
    "verse": {
      "arabic": "وَمَا أَرْسَلْنَاكَ إِلَّا كَافَّةً لِلنَّاسِ بَشِيرًا وَنَذِيرًا وَلَكِنَّ أَكْثَرَ النَّاسِ لَا يَعْلَمُونَ [سبأ : ٢٨]",
      "translationKey": "dailyContent.day28.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day28.hadith.text",
      "referenceKey": "dailyContent.day28.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ آتِ نَفْسِي تَقْوَاهَا، وَزَكِهَا أَنْتَ خَيْرُ مَنْ زَكَّاهَا ، أَنْتَ وَلِيُّهَا وَمَوْلَاهَا",
      "translationKey": "dailyContent.day28.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الرَّفِيقُ",
        "meaningKey": "dailyContent.day28.namesOfAllah.1"
      },
      {
        "arabic": "الْغَنِيُّ",
        "meaningKey": "dailyContent.day28.namesOfAllah.2"
      },
      {
        "arabic": "الرَّقِيبُ",
        "meaningKey": "dailyContent.day28.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day28.taskOfTheDay"
  },
  29: {
    "day": 29,
    "verse": {
      "arabic": "وَإِن تُطِعْ أَكْثَرَ مَن فِي الْأَرْضِ يُضِلُّوكَ عَن سَبِيلِ اللَّهِ [الأنعام : ١١٦]",
      "translationKey": "dailyContent.day29.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day29.hadith.text",
      "referenceKey": "dailyContent.day29.hadith.reference"
    },
    "dua": {
      "arabic": "يَا حَيُّ يَا قَيُّومُ بِرَحْمَتِكَ أَسْتَغِيثُ",
      "translationKey": "dailyContent.day29.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "الْمُجِيبُ",
        "meaningKey": "dailyContent.day29.namesOfAllah.1"
      },
      {
        "arabic": "الْجَوَادُ",
        "meaningKey": "dailyContent.day29.namesOfAllah.2"
      },
      {
        "arabic": "الْفَتَّاحُ",
        "meaningKey": "dailyContent.day29.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day29.taskOfTheDay"
  },
  30:{
    "day": 30,
    "verse": {
      "arabic": "رَبِّ اجْعَلْ هَٰذَا الْبَلَدَ آمِنًا وَاجْنُبْنِي وَبَنِيَّ أَنْ نَعْبُدَ الْأَصْنَامَ",
      "translationKey": "dailyContent.day30.verse"
    },
    "hadith": {
      "textKey": "dailyContent.day30.hadith.text",
      "referenceKey": "dailyContent.day30.hadith.reference"
    },
    "dua": {
      "arabic": "اللَّهُمَّ إِنِّي أَسْأَلُكَ الْجَنَّةَ وَأَعُوذُ بِكَ مِنَ النَّارِ",
      "translationKey": "dailyContent.day30.dua"
    },
    "namesOfAllah": [
      {
        "arabic": "بَدِيعُ السَّمَوَاتِ وَالْأَرْضِ",
        "meaningKey": "dailyContent.day30.namesOfAllah.1"
      },
      {
        "arabic": "الأعلى",
        "meaningKey": "dailyContent.day30.namesOfAllah.2"
      },
      {
        "arabic": "الْمَنَّانُ",
        "meaningKey": "dailyContent.day30.namesOfAllah.3"
      }
    ],
    "taskOfTheDay": "dailyContent.day30.taskOfTheDay"
  }
  // Add more days data as needed
};

// Create a helper function that accepts a translate function
const getDailyContentWithTranslation = (
  day: number, 
  translateFn: (key: string) => string
): DailyContentType => {
  // Default to day 1 if the requested day doesn't exist
  const dayData = dailyDataStructure[day] || dailyDataStructure[1];

  // Transform the data structure with translations
  return {
    verse: {
      arabic: dayData.verse.arabic,
      translationKey: dayData.verse.translationKey
    },
    hadith: {
      arabic: dayData.hadith.arabic,
      textKey: dayData.hadith.textKey,
      referenceKey: dayData.hadith.referenceKey
    },
    dua: {
      arabic: dayData.dua.arabic,
      translationKey: dayData.dua.translationKey
    },
    namesOfAllah: dayData.namesOfAllah.map(name => ({
      arabic: name.arabic,
      meaningKey: name.meaningKey
    })),
    taskOfTheDay: dayData.taskOfTheDay
  };
};

/**
 * React Hook to get translated content for a specific day 
 * This should be used within React components
 */
export const useDailyContent = (day: number): DailyContentType => {
  const { t } = useTranslation();
  return getDailyContentWithTranslation(day, t);
};

/**
 * Function to get data for a specific day
 * This maintains backward compatibility with existing code
 */
export const getDailyContent = (day: number): DailyContentType => {  
  // Use a basic translation function that falls back to the key
  const fallbackTranslate = (key: string): string => {
    // Try to get translations from the structure based on the current path
    // This is a simplified fallback solution when not used within React components
    const keyParts = key.split('.');
    if (
      keyParts.length >= 3 && 
      keyParts[0] === 'dailyContent' && 
      keyParts[1].startsWith('day')
    ) {
      const dayNumber = parseInt(keyParts[1].substring(3));
      
      // Based on the pattern, determine what we're translating
      if (keyParts[2] === 'verse') {
        return `Translation for verse of day ${dayNumber}`;
      } else if (keyParts[2] === 'hadith') {
        if (keyParts[3] === 'text') {
          return `Hadith text for day ${dayNumber}`;
        } else if (keyParts[3] === 'reference') {
          return `Hadith reference for day ${dayNumber}`;
        }
      } else if (keyParts[2] === 'dua') {
        return `Dua translation for day ${dayNumber}`;
      } else if (keyParts[2] === 'namesOfAllah') {
        return `Meaning of name ${keyParts[3]} for day ${dayNumber}`;
      }
    }
    
    return key; // Just return the key if we can't determine a fallback
  };
  
  return getDailyContentWithTranslation(day, fallbackTranslate);
};

// Function to get all available days
export const getAvailableDays = (): number[] => {
  return Object.keys(dailyDataStructure).map(day => parseInt(day));
}; 